import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';

/**
 * Employment Change Log report styling (ADR-145).
 *
 * Deliberately the SAME furniture as the rest of the reports family rather
 * than a look of its own: FiltersBar and HeaderStats are the Fixed Term
 * Contracts report's, the row is its EmployeeRow with a change-shaped right
 * cell, LoadMoreButton and SortButton are the shared neutral affordances, and
 * badges use the pastel chip palette (constants/chipPalette.js). A report that
 * looks different from its neighbours reads as a different product.
 *
 * Two devices are this report's own, and both encode something true rather
 * than decorating:
 *  - the FINDING border — a human term movement with no amendment behind it,
 *    the one row class that is a finding rather than telemetry;
 *  - the CORRELATION band — records written by one cause share a
 *    correlationId, and the band says so once instead of repeating the cause
 *    on every row.
 * Machine rows are muted, never hidden (ADR-145 §6).
 */

export const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 24px;
`;

export const HeaderStats = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  position: relative;
  padding: 0 16px;
  min-height: 36px;

  .top {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .label {
    font-size: 11px;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .value {
    font-size: 20px;
    font-weight: 700;
    color: ${({ $alarm, $clear }) => {
    if (!$alarm) return COLORS.PRIMARY;
    return $clear ? COLORS.SUCCESS : COLORS.DESTRUCTIVE;
  }};
  }

  .sub {
    font-size: 9px;
    color: #94A3B8;
    line-height: 1;
  }

  & + &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 24px;
    background: #e0e0e0;
  }
`;

/** The integrity angle, in the house SummaryTile idiom. */
export const IntegrityPanel = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    margin-bottom: 2px;
  }

  .hint {
    font-size: 12px;
    color: ${COLORS.SECONDARY};
    margin-bottom: 10px;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .boundary {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #f1f5f9;
    font-size: 12px;
    color: #94A3B8;
  }
`;

export const ChangeRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  /* The severity stripe was retired 2026-08-26 (owner's call): it shared the
     left-gutter channel with the correlation rail, so stacked finding rows
     read as a group. The red Unexplained chip carries the finding alone —
     one visual channel, one meaning. The transparent border keeps the 19px
     content alignment the notes and detail panels are tuned to. */
  border-left: 3px solid transparent;
  transition: background-color 0.15s;
  gap: 6px;
  cursor: pointer;
  opacity: ${({ $machine }) => ($machine ? 0.78 : 1)};

  &:hover {
    background-color: #f8fafc;
  }

  &:last-of-type {
    border-bottom: none;
  }
`;

export const ChangeRowMain = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

/** Mirrors EmployeeInfo so a row reads the same as every other report's row. */
export const ChangeInfo = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-size: 14px;
    font-weight: 500;
    color: ${COLORS.PRIMARY};
  }

  .name-link {
    display: inline-block;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  .detail {
    font-size: 12px;
    color: #64748B;
    margin-top: 2px;

    /* The changed field — the subject of the record, emphasised against the
       provenance segments that share the line. */
    .field {
      font-weight: 600;
      color: ${COLORS.PRIMARY};
    }
  }

  .movement {
    font-size: 13px;
    color: ${COLORS.PRIMARY};
    margin-top: 4px;

    .from { color: #94A3B8; }
    .arrow { color: ${COLORS.SECONDARY}; padding: 0 6px; }
    .to { font-weight: 500; }
    .absent { color: #94A3B8; border-bottom: 1px dotted #CBD5E1; cursor: help; }
    .gone { color: ${COLORS.DESTRUCTIVE}; }
  }
`;

/** The right-hand cell: when it was detected, and what kind of record it is. */
export const ChangeMetaCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 150px;

  .when {
    font-size: 13px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
  }

  .effective {
    font-size: 11px;
    font-weight: 500;
    color: #64748B;
    background-color: #F1F5F9;
    border: 1px solid #E2E8F0;
    border-radius: 4px;
    padding: 1px 6px;
    white-space: nowrap;
  }
`;

/**
 * The ledger-margin band (ADR-145 §6 / phase 2): records written by ONE cause
 * share a correlationId, and the group is joined by a vertical rail in the
 * left gutter — the cause is visible at a glance, stated once by the note
 * below. The rail sits INSIDE the row padding (left 6px), clear of the
 * severity stripe on the row's own left border.
 */
export const CorrelationGroup = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 12px;
    bottom: 12px;
    width: 3px;
    border-radius: 2px;
    background: #cbd5e1;
  }
`;

/** One cause, one band — the record's correlationId, said once. */
export const CorrelationNote = styled.div`
  font-size: 11px;
  color: ${COLORS.SECONDARY};
  padding: 2px 16px 4px 19px;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 10px;
    height: 1px;
    background: ${COLORS.SECONDARY};
    opacity: 0.5;
  }
`;

export const DetailPanel = styled.div`
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  padding: 12px 16px 14px 19px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px 20px;

  dt {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${COLORS.SECONDARY};
    margin-bottom: 2px;
  }

  dd {
    margin: 0;
    font-size: 12px;
    color: ${COLORS.PRIMARY};
    overflow-wrap: anywhere;
  }

  .note {
    grid-column: 1 / -1;
    font-size: 12px;
    color: #475569;
    line-height: 1.5;
    margin: 0;
  }
`;

export const LoadMoreButton = styled.button`
  display: block;
  margin: 16px auto;
  padding: 8px 24px;
  background: #f1f5f9;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    border-color: #94A3B8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SortButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #64748B;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: #f1f5f9;
    color: ${COLORS.PRIMARY};
  }

  svg {
    font-size: 12px;
  }
`;

export const FootNote = styled.p`
  margin: 20px 0 0;
  font-size: 12px;
  color: #94A3B8;
  line-height: 1.55;
  max-width: 80ch;
`;

// ADR-112 — the amendments lane: EVENT-sourced (never change records, ADR-147
// §7), rendered as its own bounded panel above the record list with its own
// counts. The amber left rail matches the Pending-amendment treatment the
// Episodes tab uses: this panel is about intents, not movements.
export const AmendmentsLanePanel = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-left: 3px solid #F59E0B;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    margin-bottom: 2px;
  }

  .caption {
    font-size: 12px;
    color: #94A3B8;
    margin-bottom: 6px;
  }

  .empty {
    font-size: 13px;
    color: ${COLORS.SECONDARY};
    padding: 6px 0 2px;
  }

  .sub-title {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${COLORS.SECONDARY};
    margin: 12px 0 2px;
  }

  .lane-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-top: 1px solid #f1f5f9;

    .main {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .name {
      font-size: 13px;
      font-weight: 600;
      color: ${COLORS.PRIMARY};
      cursor: pointer;
      width: fit-content;

      &:hover { text-decoration: underline; }
    }

    .phrase {
      font-size: 13px;
      color: #334155;

      /* The amended field — same emphasis the record cards give it. */
      .field {
        font-weight: 600;
        color: ${COLORS.PRIMARY};
      }
    }

    .sub {
      font-size: 12px;
      color: #94A3B8;
    }

    .side {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
    }
  }
`;
